<div class="privacy-policy-container bg-gray-50 min-h-screen">
  <!-- Hero Section -->
  <section class="bg-gradient-to-r from-immovea-navy via-blue-800 to-blue-900 text-white py-12 sm:py-20 lg:py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl">
        <h1 class="text-4xl sm:text-5xl font-bold mb-3 sm:mb-4">Privacy Policy</h1>
        <p class="text-base sm:text-xl text-blue-100 leading-relaxed">Your privacy is important to us. Learn how we collect, use, and protect your personal information.</p>
        <p class="text-sm text-blue-200 mt-4">Last Updated: {{ lastUpdated }}</p>
      </div>
    </div>
  </section>

  <!-- Content Section -->
  <div class="max-w-4xl mx-auto py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
    <div class="bg-white rounded-xl shadow-lg p-6 sm:p-10 space-y-8">
      
      <!-- Introduction -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
        <p class="text-gray-700 leading-relaxed">
          IMMOVÉA ("we," "our," or "us") operates immovea.realestate and is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services, in compliance with Lebanese data protection laws and regulations.
        </p>
        <p class="text-gray-700 leading-relaxed mt-3">
          By using our website, you consent to the data practices described in this policy. If you do not agree with this policy, please discontinue use of our services.
        </p>
      </section>

      <!-- Information We Collect -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
        
        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">2.1 Personal Information</h3>
        <p class="text-gray-700 leading-relaxed mb-3">We may collect personal information that you voluntarily provide to us, including:</p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>Full name</li>
          <li>Email address</li>
          <li>Phone number and country code</li>
          <li>Property preferences and requirements</li>
          <li>Budget information</li>
          <li>Communication preferences</li>
          <li>Any other information you choose to provide through our contact forms</li>
        </ul>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Automatically Collected Information</h3>
        <p class="text-gray-700 leading-relaxed mb-3">When you visit our website, we automatically collect certain information, including:</p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>IP address and location data</li>
          <li>Browser type and version</li>
          <li>Device information</li>
          <li>Pages visited and time spent on pages</li>
          <li>Referring website addresses</li>
          <li>Date and time of visits</li>
        </ul>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">2.3 Cookies and Tracking Technologies</h3>
        <p class="text-gray-700 leading-relaxed">
          We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand user preferences. You can control cookie settings through your browser preferences.
        </p>
      </section>

      <!-- How We Use Your Information -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
        <p class="text-gray-700 leading-relaxed mb-3">We use the collected information for the following purposes:</p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>To respond to your inquiries and provide customer support</li>
          <li>To process property quotes and valuation requests</li>
          <li>To match you with suitable properties based on your preferences</li>
          <li>To send you property listings and market updates (with your consent)</li>
          <li>To improve our website and services</li>
          <li>To prevent fraud and ensure security</li>
          <li>To comply with legal obligations under Lebanese law</li>
          <li>To analyze website usage and optimize user experience</li>
          <li>To send administrative information about our services</li>
        </ul>
      </section>

      <!-- Information Sharing -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
        <p class="text-gray-700 leading-relaxed mb-3">We do not sell or rent your personal information. We may share your information in the following circumstances:</p>
        
        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">4.1 Service Providers</h3>
        <p class="text-gray-700 leading-relaxed">
          We may share information with trusted third-party service providers who assist us in operating our website, conducting business, or serving you, provided they agree to keep information confidential.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Legal Requirements</h3>
        <p class="text-gray-700 leading-relaxed">
          We may disclose your information when required by Lebanese law, regulation, legal process, or governmental request, or to protect our rights, property, or safety, or that of others.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 Business Transfers</h3>
        <p class="text-gray-700 leading-relaxed">
          In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.
        </p>
      </section>

      <!-- Data Security -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
        <p class="text-gray-700 leading-relaxed">
          We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include:
        </p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
          <li>Secure socket layer (SSL) encryption for data transmission</li>
          <li>Secure cloud storage with access controls</li>
          <li>Regular security assessments and updates</li>
          <li>Limited employee access to personal data</li>
          <li>Data backup and recovery procedures</li>
        </ul>
        <p class="text-gray-700 leading-relaxed mt-3">
          However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
        </p>
      </section>

      <!-- Data Retention -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
        <p class="text-gray-700 leading-relaxed">
          We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by Lebanese law. When information is no longer needed, we will securely delete or anonymize it.
        </p>
      </section>

      <!-- Your Rights -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
        <p class="text-gray-700 leading-relaxed mb-3">Under Lebanese law and international privacy standards, you have the following rights regarding your personal information:</p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li><strong>Right to Access:</strong> Request a copy of the personal information we hold about you</li>
          <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete information</li>
          <li><strong>Right to Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
          <li><strong>Right to Object:</strong> Object to processing of your personal information for direct marketing purposes</li>
          <li><strong>Right to Withdraw Consent:</strong> Withdraw your consent at any time where processing is based on consent</li>
          <li><strong>Right to Data Portability:</strong> Request transfer of your data to another service provider</li>
        </ul>
        <p class="text-gray-700 leading-relaxed mt-4">
          To exercise any of these rights, please contact us at immovea.realestate&#64;gmail.com. We will respond to your request within 30 days.
        </p>
      </section>

      <!-- Third-Party Links -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Websites</h2>
        <p class="text-gray-700 leading-relaxed">
          Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
        </p>
      </section>

      <!-- Children's Privacy -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
        <p class="text-gray-700 leading-relaxed">
          Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete such information promptly.
        </p>
      </section>

      <!-- International Data Transfers -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">10. International Data Transfers</h2>
        <p class="text-gray-700 leading-relaxed">
          Your information may be transferred to and processed in countries other than Lebanon. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.
        </p>
      </section>

      <!-- Changes to Privacy Policy -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
        <p class="text-gray-700 leading-relaxed">
          We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
        </p>
      </section>

      <!-- Contact Information -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
        </p>
        <div class="bg-blue-50 border-l-4 border-immovea-navy p-6 rounded-r-lg">
          <p class="text-gray-800 font-semibold mb-2">IMMOVÉA Real Estate</p>
          <p class="text-gray-700">Email: immovea.realestate&#64;gmail.com</p>
          <p class="text-gray-700">Website: immovea.realestate</p>
          <p class="text-gray-700 mt-2">Lebanon</p>
        </div>
      </section>

      <!-- Lebanese Law Compliance -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">13. Compliance with Lebanese Law</h2>
        <p class="text-gray-700 leading-relaxed">
          This Privacy Policy is designed to comply with Lebanese data protection principles and regulations. We operate in accordance with Lebanese Law 81/2018 on Electronic Transactions and Personal Data, and we respect the privacy rights granted to individuals under Lebanese law and international standards.
        </p>
      </section>

    </div>
  </div>
</div>
